/**
 * Auth initialization hook.
 * Decodes the stored JWT on app load, validates expiry, and populates the store.
 * Redirects to /login if the token is absent or expired.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import type { AuthUser } from '../types/api';
import { useAppStore } from '../store/appStore';

/**
 * Reads the JWT from localStorage, validates expiry, and writes the user to the store.
 * Must be called once from the root app layout — it handles the initial auth check.
 */
export function useAuth(): void {
  const { token, setAuth, logout } = useAppStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const decoded = jwtDecode<AuthUser & { exp: number }>(token);
      const nowSec = Math.floor(Date.now() / 1000);

      if (decoded.exp < nowSec) {
        logout();
        navigate('/login');
        return;
      }

      setAuth(token, decoded);
    } catch {
      logout();
      navigate('/login');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
